/**
 * Transport & Moving Page
 * Service category page for transport requests.
 *
 * i18n: all visible text (including card labels/desc/examples) is driven
 * by `t()` from the locale bundle. There are NO inline `{en,et,fi}`
 * labels — that pattern leaked English when the user selected `nb`.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { GlobalNav } from '@/components/GlobalNav';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Truck, MapPin, ArrowRight, Package, Home,
  Zap, CheckCircle, Shield,
} from 'lucide-react';

// Keyed into the `transport.*` namespace — locale-agnostic structure.
const TRANSPORT_TYPES = [
  { id: 'local', icon: Truck, labelKey: 'typeLocalLabel', descKey: 'typeLocalDesc', exampleKey: 'typeLocalExample' },
  { id: 'long-distance', icon: MapPin, labelKey: 'typeLongLabel', descKey: 'typeLongDesc', exampleKey: 'typeLongExample' },
  { id: 'moving', icon: Home, labelKey: 'typeMovingLabel', descKey: 'typeMovingDesc', exampleKey: 'typeMovingExample' },
  { id: 'delivery', icon: Package, labelKey: 'typeDeliveryLabel', descKey: 'typeDeliveryDesc', exampleKey: 'typeDeliveryExample' },
];

const FEATURE_KEYS = [
  'featureInstantQuotes',
  'featureVerifiedDrivers',
  'featureRealtimeTracking',
  'featureInsuranceIncluded',
];

const TransportPage = () => {
  const { t } = useLanguage();
  const tx = (key) => t(`transport.${key}`);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <GlobalNav />

      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-blue-100 text-blue-700">
            <Truck className="h-3.5 w-3.5 mr-1" />
            {tx('badge')}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-testid="transport-page-title">
            {tx('pageTitle')}
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-6" data-testid="transport-page-subtitle">
            {tx('pageSubtitle')}
          </p>
          <Link to="/booking?category=transport">
            <Button size="lg" className="bg-blue-500 hover:bg-blue-600" data-testid="transport-cta">
              {tx('pageCta')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>

        {/* Features */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {FEATURE_KEYS.map((k) => (
            <div key={k} className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">{tx(k)}</span>
            </div>
          ))}
        </div>

        {/* Transport Types */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {TRANSPORT_TYPES.map((type) => {
            const Icon = type.icon;
            return (
              <Card
                key={type.id}
                className="hover:shadow-lg transition-shadow border-2 hover:border-blue-200"
              >
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-100 rounded-xl">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{tx(type.labelKey)}</CardTitle>
                      <CardDescription>{tx(type.descKey)}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500 italic mb-4">{tx(type.exampleKey)}</p>
                  <Link to={`/transport/${type.id}`}>
                    <Button variant="outline" className="w-full" data-testid={`transport-${type.id}-btn`}>
                      {tx('request')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Trust Banner */}
        <Card className="bg-gradient-to-r from-blue-600 to-blue-800 text-white border-0">
          <CardContent className="py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <Shield className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{tx('trustTitle')}</h3>
                  <p className="text-blue-100">{tx('trustDesc')}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <Zap className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{tx('quickTitle')}</h3>
                  <p className="text-blue-100">{tx('quickDesc')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default TransportPage;
